import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  requestTypesQuery,
  resetRequestType,
  saveRequestType,
  type AiRequestTypeFields,
  type AiRequestTypeRow,
  type AiRequestTypeScope,
  type AiRequestTypesPage,
} from '../../api/settingsAiAgent'
import { Spinner } from '../../ui/Spinner'
import { failed, NoteText, type Note } from './parts'

/**
 * Fila "Request types": politica biroului AI pe tip de cerere.
 *
 * Lista e `Taxonomy::TYPES` din PHP, in aceeasi ordine; `tools/check_desk.py`
 * o compara cu `REQUEST_TYPES` de aici si cu seed-ul migrarii.
 */
export const REQUEST_TYPES: { request_type: string; label: string }[] = [
  { request_type: 'question', label: 'Question' },
  { request_type: 'diagnose', label: 'Diagnose a problem' },
  { request_type: 'software_install', label: 'Install software' },
  { request_type: 'software_remove', label: 'Remove software' },
  { request_type: 'runbook_own', label: 'Runbook on own device' },
  { request_type: 'runbook_person', label: 'Runbook about the person' },
  { request_type: 'message', label: 'Message on screen' },
  { request_type: 'password_local', label: 'Local password' },
  { request_type: 'power_own', label: 'Restart own device' },
  { request_type: 'disk_full', label: 'Disk full' },
  { request_type: 'printer', label: 'Printer' },
  { request_type: 'outlook', label: 'Outlook' },
  { request_type: 'teams', label: 'Teams' },
  { request_type: 'vpn', label: 'VPN' },
  { request_type: 'slow_pc', label: 'Slow PC' },
  { request_type: 'network_device', label: 'Network device' },
  { request_type: 'server', label: 'Server' },
  { request_type: 'hardware', label: 'Hardware' },
  { request_type: 'purchase', label: 'Purchase' },
  { request_type: 'license', label: 'License' },
  { request_type: 'ad_unlock', label: 'AD account unlock' },
  { request_type: 'ad_password', label: 'AD password' },
  { request_type: 'group_membership', label: 'Group membership' },
  { request_type: 'mailbox_access', label: 'Mailbox access' },
  { request_type: 'm365_sessions', label: 'M365 sessions' },
  { request_type: 'm365_mfa', label: 'M365 MFA' },
  { request_type: 'fleet_change', label: 'Fleet change' },
  { request_type: 'onboarding', label: 'Onboarding' },
  { request_type: 'offboarding', label: 'Offboarding' },
  { request_type: 'other', label: 'Other' },
]

const LABELS: Record<string, string> = Object.fromEntries(REQUEST_TYPES.map((t) => [t.request_type, t.label]))

const SOURCE_TEXT: Record<string, string> = {
  seed: 'platform default',
  provider: 'provider setting',
  company: 'set for this company',
}

export function RequestTypesTab({ scope }: { scope: AiRequestTypeScope }) {
  const query = useQuery(requestTypesQuery(scope))
  const client = useQueryClient()
  const page = query.data

  if (query.isError) return <p className="text-danger">{failed(query.error)}</p>
  if (!page) return <Spinner />
  if (!page.ok) return <p className="text-danger">{page.error ?? 'Failed'}</p>

  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'ai_agent', 'request_types', scope] })

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <strong>Request types</strong>
        <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
          {scope === 'global'
            ? 'provider defaults for the AI service desk, per request type - every company inherits them'
            : "this company's policy per request type - a company row beats the provider's"}
          . "Let triage choose" leaves the ticket type, category or priority to the desk's triage.
        </span>
      </div>
      <div className="panel-body" style={{ overflowX: 'auto' }}>
        {page.warning ? <p className="text-danger">{page.warning}</p> : null}
        <table className="table table-condensed" style={{ fontSize: 12, margin: 0 }}>
          <thead>
            <tr>
              <th>Request type</th>
              <th>Ticket type</th>
              <th>Category / subcategory</th>
              <th>Priority</th>
              <th>Verifier</th>
              <th>Tier</th>
              <th>Required info</th>
              <th>Allowed outcomes</th>
              <th title="Used from F5 (authority matrix)">Confirm</th>
              <th title="Used from F5 (authority matrix)">Min trust</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {REQUEST_TYPES.map((t) => {
              const row = page.rows.find((r) => r.request_type === t.request_type)

              return row ? <TypeRow key={`${t.request_type}:${row.effective.source}`} page={page} row={row} scope={scope} onSaved={reload} /> : null
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Valorile din care porneste randul: ale scopului, sau ce mosteneste. */
function startFrom(row: AiRequestTypeRow, scope: AiRequestTypeScope): AiRequestTypeFields {
  if (scope === 'company') return row.company ?? row.effective

  return row.provider ?? row.defaults
}

function TypeRow({
  page,
  row,
  scope,
  onSaved,
}: {
  page: AiRequestTypesPage
  row: AiRequestTypeRow
  scope: AiRequestTypeScope
  onSaved(): void
}) {
  const [d, setD] = useState<AiRequestTypeFields>(() => startFrom(row, scope))
  const [note, setNote] = useState<Note | null>(null)
  const label = LABELS[row.request_type] ?? row.request_type
  const own = scope === 'company' ? row.company !== null : row.provider !== null
  const paths = d.allowed_paths.split(',').filter((p) => p !== '')
  const category = page.kinds.categories.find((c) => c.id === d.ticket_category)
  const set = (patch: Partial<AiRequestTypeFields>) => setD((cur) => ({ ...cur, ...patch }))

  const togglePath = (p: string, on: boolean) => {
    const next = on ? [...paths, p] : paths.filter((x) => x !== p)
    set({ allowed_paths: page.paths.filter((x) => next.includes(x)).join(',') })
  }

  const save = () => {
    setNote({ text: '…' })
    void saveRequestType({
      scope,
      request_type: row.request_type,
      ticket_type: d.ticket_type,
      ticket_category: d.ticket_category,
      ticket_subcategory: d.ticket_subcategory,
      ticket_priority: d.ticket_priority,
      verify_key: d.verify_key ?? '',
      tier: d.tier,
      required_info: d.required_info,
      allowed_paths: d.allowed_paths,
      needs_confirmation: d.needs_confirmation,
      min_trust: d.min_trust,
    })
      .then((r) => {
        setNote(r.ok ? { text: 'Saved' } : { text: r.error ?? 'Failed', bad: true })
        if (r.ok) onSaved()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const reset = () => {
    setNote({ text: '…' })
    void resetRequestType({ scope, request_type: row.request_type })
      .then((r) => {
        setNote(r.ok ? { text: scope === 'company' ? 'Back to the provider' : 'Back to the default' } : { text: r.error ?? 'Failed', bad: true })
        if (r.ok) onSaved()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <tr>
      <td style={{ minWidth: 150 }}>
        <strong>{label}</strong>
        <div className="text-muted" style={{ fontSize: 11 }}>
          {row.request_type} · {row.resolver} · {SOURCE_TEXT[row.effective.source] ?? row.effective.source}
        </div>
      </td>
      <td>
        <select
          className="form-control input-sm"
          aria-label={`${label} ticket type`}
          style={{ minWidth: 130 }}
          value={d.ticket_type}
          onChange={(e) => set({ ticket_type: Number(e.target.value) })}
        >
          <option value={0}>Let triage choose</option>
          {page.kinds.types.map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
          {d.ticket_type > 0 && !page.kinds.types.some((k) => k.id === d.ticket_type) ? (
            <option value={d.ticket_type} disabled>
              #{d.ticket_type} (not active)
            </option>
          ) : null}
        </select>
      </td>
      <td>
        <select
          className="form-control input-sm"
          aria-label={`${label} category`}
          style={{ minWidth: 150 }}
          value={d.ticket_category}
          onChange={(e) => set({ ticket_category: Number(e.target.value), ticket_subcategory: 0 })}
        >
          <option value={0}>Let triage choose</option>
          {page.kinds.categories.map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
        </select>
        {category && category.subcategories.length > 0 ? (
          <select
            className="form-control input-sm"
            aria-label={`${label} subcategory`}
            style={{ marginTop: 4, minWidth: 150 }}
            value={d.ticket_subcategory}
            onChange={(e) => set({ ticket_subcategory: Number(e.target.value) })}
          >
            <option value={0}>Any subcategory</option>
            {category.subcategories.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        ) : null}
      </td>
      <td>
        <select
          className="form-control input-sm"
          aria-label={`${label} priority`}
          style={{ minWidth: 120 }}
          value={d.ticket_priority}
          onChange={(e) => set({ ticket_priority: Number(e.target.value) })}
        >
          <option value={0}>Matrix / triage</option>
          {page.kinds.priorities.map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select
          className="form-control input-sm"
          aria-label={`${label} verifier`}
          style={{ minWidth: 120 }}
          value={d.verify_key ?? ''}
          onChange={(e) => set({ verify_key: e.target.value === '' ? null : e.target.value })}
        >
          <option value="">none</option>
          {page.verify_keys.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select className="form-control input-sm" aria-label={`${label} tier`} style={{ minWidth: 80 }} value={d.tier} onChange={(e) => set({ tier: e.target.value })}>
          {page.tiers.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input
          type="text"
          className="form-control input-sm"
          aria-label={`${label} required info`}
          style={{ minWidth: 110 }}
          title={'Comma separated: ' + page.required_info.join(', ')}
          value={d.required_info}
          onChange={(e) => set({ required_info: e.target.value })}
        />
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {page.paths.map((p) => (
          <label key={p} style={{ fontWeight: 400, marginRight: 6 }}>
            <input
              type="checkbox"
              aria-label={`${label} outcome ${p}`}
              checked={paths.includes(p)}
              disabled={p === 'answer'}
              onChange={(e) => togglePath(p, e.target.checked)}
            />{' '}
            {p}
          </label>
        ))}
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          aria-label={`${label} needs confirmation`}
          checked={d.needs_confirmation === 1}
          onChange={(e) => set({ needs_confirmation: e.target.checked ? 1 : 0 })}
        />
      </td>
      <td>
        <select
          className="form-control input-sm"
          aria-label={`${label} minimum trust`}
          style={{ minWidth: 56 }}
          value={d.min_trust}
          onChange={(e) => set({ min_trust: Number(e.target.value) })}
        >
          {[0, 1, 2, 3].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button type="button" className="btn btn-primary btn-xs" disabled={!page.ready} onClick={save}>
          Save
        </button>{' '}
        {own ? (
          <button type="button" className="btn btn-default btn-xs" onClick={reset}>
            {scope === 'company' ? 'Reset to provider' : 'Reset to default'}
          </button>
        ) : null}
        <div style={{ fontSize: 11 }}>
          <NoteText note={note} />
        </div>
      </td>
    </tr>
  )
}
